import { Fragment, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  api, PriceComponent, PricedAction, PricingTable, ProviderCostRow, ProviderCurrency,
} from '../services/api';
import { invalidateCreditPricingCache } from '../hooks/useCreditPricing';
import { LlmAreaModels } from './LlmAreaModels';

// Tabela ÚNICA de preços da plataforma (super admin › Preços).
//
// Tudo nasce do custo de provedor: créditos = custo × câmbio (se US$) × markup ÷ valor do crédito.
// - Funcionalidades: cada ação (consulta CNPJ, interação do AgentCRM-IA, conexão…) é uma receita
//   de componentes — linha de custo × quantidade, ou tokens médios no modelo da área.
// - Custos de provedor: as linhas em si (LLM por família, imagem, vídeo, Apify, Serper…).
// - Moeda "Créditos" = a linha já é preço (mensalidade de conexão): sem câmbio nem markup.
// Backend: services/creditPricing.ts, providerCostService.ts e llmAreaConfig.ts.

const PROVIDER_LABELS: Record<string, string> = {
  omniroute: 'OmniRoute (GPT, Claude, HeyGen, codexgpt)',
  snapgen: 'SnapGen',
  fal: 'fal.ai',
  google: 'Google (Gemini)',
  groq: 'Groq (transcrição)',
  openai: 'OpenAI (Whisper)',
  apify: 'Apify',
  serper: 'Serper (Google Maps)',
  casadados: 'Casa dos Dados (CNPJ)',
  directdata: 'Direct Data (ERP)',
  allcance: 'Allcance (WhatsApp de terceiros)',
  fish: 'Fish Audio (voz)',
  plataforma: 'Plataforma (mensalidades)',
};

const GROUP_LABELS: Record<PricedAction['group'], string> = {
  crm: 'CRM e campanhas',
  leads: 'Leads (Google, CNPJ, ERP)',
  seller: 'Seller',
  voz: 'Voz',
  marketing: 'Marketing Studio',
  conexoes: 'Conexões (mensalidade)',
};
const GROUP_ORDER: PricedAction['group'][] = ['crm', 'leads', 'seller', 'voz', 'marketing', 'conexoes'];

const CURRENCY_LABEL: Record<ProviderCurrency, string> = { BRL: 'R$', USD: 'US$', CREDITO: 'Créditos' };

function formatNum(n: number, max = 4) {
  return Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: max });
}
function parseNum(raw: string): number {
  return Number(String(raw ?? '').replace(',', '.').trim());
}
function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

// Mesma regra do backend (providerCostService.modelFamily), para a prévia ao vivo.
function modelFamily(model: string): string {
  const id = String(model || '').toLowerCase();
  if (id.includes('gemini-3.8-flash')) return 'gemini_38_flash';
  if (/gpt-6(\.\d+)?-luna/.test(id)) return 'llm_luna';
  if (/gpt-6(\.\d+)?-sol/.test(id)) return 'llm_sol';
  if (/gpt-6(\.\d+)?-astra/.test(id)) return 'llm_astra';
  if (/(^|\/)claude[-/]/.test(id) || id.startsWith('cc/')) return id.includes('sonnet') ? 'llm_sonnet' : 'llm_opus';
  return 'llm_sol';
}

type CompDraft = { rate?: string; qty?: string; llm?: string; in?: string; cache?: string; out?: string };

function toDraft(c: PriceComponent | any): CompDraft {
  if (c.llm) return { llm: c.llm, in: String(c.in ?? 0), cache: String(c.cache ?? 0), out: String(c.out ?? 0) };
  return { rate: c.rate, qty: String(c.qty ?? 0) };
}
function fromDraft(d: CompDraft): PriceComponent {
  if (d.llm) return { llm: d.llm, in: parseNum(d.in || '0'), cache: parseNum(d.cache || '0'), out: parseNum(d.out || '0') };
  return { rate: d.rate!, qty: parseNum(d.qty || '0') };
}

export function PricingTableManager() {
  const [table, setTable] = useState<PricingTable | null>(null);
  const [loading, setLoading] = useState(true);
  const [settingsDraft, setSettingsDraft] = useState({ usdBrl: '', markup: '', creditValueBrl: '' });
  const [drafts, setDrafts] = useState<Record<string, { cost: string; currency: ProviderCurrency }>>({});
  const [actionDrafts, setActionDrafts] = useState<Record<string, CompDraft[]>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<{ key: string; cost: string; date: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const apply = (data: PricingTable) => {
    setTable(data);
    setSettingsDraft({
      usdBrl: String(data.settings.usdBrl),
      markup: String(data.settings.markup),
      creditValueBrl: String(data.settings.creditValueBrl),
    });
    const d: Record<string, { cost: string; currency: ProviderCurrency }> = {};
    for (const r of data.rates) d[r.id] = { cost: String(r.cost), currency: r.currency };
    setDrafts(d);
    const a: Record<string, CompDraft[]> = {};
    for (const [k, act] of Object.entries(data.actions || {})) a[k] = act.components.map(toDraft);
    setActionDrafts(a);
  };

  const load = async () => {
    try {
      const res = await api.getPricingTable();
      if (res.success) apply(res.data);
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao carregar a tabela de preços');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const reloadAfterChange = async () => {
    invalidateCreditPricingCache();
    await load();
  };

  const previewSettings = useMemo(() => ({
    usdBrl: parseNum(settingsDraft.usdBrl) || table?.settings.usdBrl || 0,
    markup: parseNum(settingsDraft.markup) || table?.settings.markup || 0,
    creditValueBrl: parseNum(settingsDraft.creditValueBrl) || table?.settings.creditValueBrl || 1,
  }), [settingsDraft, table]);

  const rowPreview = (r: ProviderCostRow) => {
    const draft = drafts[r.id];
    const cost = draft ? parseNum(draft.cost) : r.cost;
    const currency = draft?.currency || r.currency;
    if (!Number.isFinite(cost)) return null;
    if (currency === 'CREDITO') return { brl: cost * previewSettings.creditValueBrl, credits: cost };
    const brl = currency === 'USD' ? cost * previewSettings.usdBrl : cost;
    const markup = r.markupOverride ?? previewSettings.markup;
    return { brl, credits: (brl * markup) / previewSettings.creditValueBrl };
  };

  // Créditos por unidade das linhas vigentes, com os rascunhos aplicados (prévia ao vivo).
  const currentCredits = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of table?.rates || []) {
      if (!r.current) continue;
      const p = rowPreview(r);
      if (p) map[r.key] = p.credits;
    }
    return map;
  }, [table, drafts, previewSettings]);

  const areaModel = (area: string) => table?.areas.find(a => a.key === area)?.model || '';

  const componentCredits = (d: CompDraft) => {
    if (d.llm) {
      const fam = modelFamily(areaModel(d.llm));
      const f = ['_in', '_cache', '_out'].every(s => currentCredits[`${fam}${s}`] !== undefined) ? fam : 'llm_sol';
      return ((currentCredits[`${f}_in`] || 0) * parseNum(d.in || '0')
        + (currentCredits[`${f}_cache`] || 0) * parseNum(d.cache || '0')
        + (currentCredits[`${f}_out`] || 0) * parseNum(d.out || '0')) / 1e6;
    }
    return (currentCredits[d.rate!] || 0) * parseNum(d.qty || '0');
  };
  const actionPreview = (key: string) => (actionDrafts[key] || []).reduce((s, d) => s + componentCredits(d), 0);

  const saveSettings = async () => {
    const payload = {
      usdBrl: parseNum(settingsDraft.usdBrl),
      markup: parseNum(settingsDraft.markup),
      creditValueBrl: parseNum(settingsDraft.creditValueBrl),
    };
    if (Object.values(payload).some(v => !Number.isFinite(v) || v <= 0)) {
      toast.error('Câmbio, markup e valor do crédito precisam ser números positivos.');
      return;
    }
    setBusy('settings');
    try {
      await api.updateProviderCostSettings(payload);
      toast.success('Parâmetros atualizados.');
      await reloadAfterChange();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao salvar parâmetros');
    } finally {
      setBusy(null);
    }
  };

  const saveRate = async (r: ProviderCostRow) => {
    const draft = drafts[r.id];
    const cost = parseNum(draft?.cost);
    if (!Number.isFinite(cost) || cost < 0) {
      toast.error('Informe um custo numérico não-negativo.');
      return;
    }
    setBusy(r.id);
    try {
      await api.updateProviderCostRate(r.id, { cost, currency: draft.currency });
      toast.success('Custo atualizado.');
      await reloadAfterChange();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao salvar custo');
    } finally {
      setBusy(null);
    }
  };

  const toggleActive = async (r: ProviderCostRow) => {
    setBusy(r.id);
    try {
      await api.updateProviderCostRate(r.id, { active: !r.active });
      await reloadAfterChange();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao atualizar');
    } finally {
      setBusy(null);
    }
  };

  const saveSchedule = async () => {
    if (!schedule) return;
    const cost = parseNum(schedule.cost);
    if (!Number.isFinite(cost) || cost < 0 || !schedule.date) {
      toast.error('Informe o custo e a data de vigência.');
      return;
    }
    setBusy(`schedule:${schedule.key}`);
    try {
      await api.scheduleProviderCostRate(schedule.key, { cost, effectiveFrom: `${schedule.date}T00:00:00.000Z` });
      setSchedule(null);
      toast.success('Novo preço agendado.');
      await reloadAfterChange();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao agendar preço');
    } finally {
      setBusy(null);
    }
  };

  const saveAction = async (key: string) => {
    const comps = (actionDrafts[key] || []).map(fromDraft);
    if (comps.some(c => ('qty' in c ? !Number.isFinite(c.qty) || c.qty < 0 : [c.in, c.cache ?? 0, c.out].some(v => !Number.isFinite(v) || v < 0)))) {
      toast.error('Quantidades e tokens precisam ser números não-negativos.');
      return;
    }
    setBusy(`action:${key}`);
    try {
      await api.updateCreditPricing(key, comps);
      toast.success('Receita salva.');
      await reloadAfterChange();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao salvar');
    } finally {
      setBusy(null);
    }
  };

  const resetAction = async (key: string) => {
    setBusy(`action:${key}`);
    try {
      await api.resetCreditPricing(key);
      toast.success('Receita padrão restaurada.');
      await reloadAfterChange();
    } catch (e: any) {
      toast.error(e?.message || 'Erro ao restaurar');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <p className="text-sm text-gray-500 animate-pulse p-4">Carregando tabela de preços...</p>;
  if (!table) return null;

  const providers = Array.from(new Set(table.rates.map(r => r.provider)));
  const actionsByGroup = GROUP_ORDER.map(g => ({
    group: g,
    rows: Object.entries(table.actions).filter(([, a]) => a.group === g),
  })).filter(x => x.rows.length);

  const inputCls = 'px-2 py-1 border border-gray-300 rounded-md font-mono text-right text-xs focus:ring-2 focus:ring-blue-500 outline-none';

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Tabela de preços</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Todo preço nasce do custo de provedor: créditos = custo × câmbio (se US$) × markup ÷ valor do crédito.
            As funcionalidades com IA seguem o modelo escolhido para cada área (abaixo). Mudanças valem para todos os tenants em até 1 minuto.
          </p>
        </div>

        <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-end gap-4">
          {([
            ['usdBrl', 'Câmbio (R$ por US$)'],
            ['markup', 'Markup (×)'],
            ['creditValueBrl', 'Valor do crédito (R$)'],
          ] as const).map(([field, label]) => (
            <label key={field} className="text-xs font-medium text-gray-600">
              {label}
              <input
                type="text"
                inputMode="decimal"
                value={settingsDraft[field]}
                onChange={e => setSettingsDraft(prev => ({ ...prev, [field]: e.target.value }))}
                className="mt-1 block w-32 px-2 py-1.5 border border-gray-300 rounded-md font-mono text-right text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </label>
          ))}
          <button
            onClick={saveSettings}
            disabled={busy === 'settings'}
            className="px-3 py-2 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
          >
            {busy === 'settings' ? '...' : 'Salvar parâmetros'}
          </button>
        </div>

        {/* Funcionalidades: receita sobre os custos */}
        <div className="p-4 space-y-6">
          <h3 className="text-sm font-semibold text-gray-800">Funcionalidades (preço por ação)</h3>
          {actionsByGroup.map(({ group, rows }) => (
            <div key={group}>
              <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">{GROUP_LABELS[group]}</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                      <th className="py-2 pr-3">Funcionalidade</th>
                      <th className="py-2 px-3">Composição</th>
                      <th className="py-2 px-3 text-right">Custo (R$)</th>
                      <th className="py-2 px-3 text-right">Créditos</th>
                      <th className="py-2 pl-3 text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(([key, a]) => {
                      const preview = actionPreview(key);
                      const dirty = Math.abs(preview - a.cost) > 0.00005;
                      const isOpen = open === key;
                      return (
                        <Fragment key={key}>
                          <tr className="border-b border-gray-50">
                            <td className="py-2 pr-3">
                              <div className="font-medium text-gray-800">
                                {a.label}
                                {a.custom && <span className="ml-2 text-[10px] rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 font-semibold">editado</span>}
                              </div>
                              <div className="text-[11px] text-gray-400">por {a.unit} · <span className="font-mono">{key}</span></div>
                            </td>
                            <td className="py-2 px-3 text-xs text-gray-500">
                              {a.components.map((c, i) => (
                                <div key={i}>
                                  {c.kind === 'llm'
                                    ? <>{c.areaLabel}: <code className="text-[10px]">{c.model}</code> · {formatNum(c.in || 0, 0)} entrada / {formatNum(c.out || 0, 0)} saída</>
                                    : <>{formatNum(c.qty || 0)} × {c.rateLabel || c.rate}{c.missing && <span className="ml-1 text-red-600 font-semibold">(linha ausente)</span>}</>}
                                </div>
                              ))}
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-gray-500">{formatNum(a.costBrl, 6)}</td>
                            <td className="py-2 px-3 text-right font-mono font-semibold text-gray-800">
                              {formatNum(preview, 4)}
                              {dirty && <div className="text-[10px] text-amber-600 font-normal">salvo: {formatNum(a.cost, 4)}</div>}
                            </td>
                            <td className="py-2 pl-3">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => setOpen(isOpen ? null : key)}
                                  className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                                >
                                  {isOpen ? 'Fechar' : 'Editar'}
                                </button>
                                {a.custom && (
                                  <button
                                    onClick={() => resetAction(key)}
                                    disabled={busy === `action:${key}`}
                                    className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                  >
                                    Padrão
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="bg-gray-50/60 border-b border-gray-100">
                              <td colSpan={5} className="px-3 py-3">
                                <div className="space-y-2">
                                  {(actionDrafts[key] || []).map((d, i) => {
                                    const set = (patch: Partial<CompDraft>) => setActionDrafts(prev => ({
                                      ...prev,
                                      [key]: prev[key].map((x, j) => (j === i ? { ...x, ...patch } : x)),
                                    }));
                                    const area = d.llm ? table.areas.find(x => x.key === d.llm) : null;
                                    return (
                                      <div key={i} className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                                        {d.llm ? (
                                          <>
                                            <span className="font-medium text-gray-800 min-w-[220px]">
                                              {area?.label || d.llm} <code className="text-[10px] text-gray-500">{area?.model}</code>
                                            </span>
                                            <label>entrada <input value={d.in} onChange={e => set({ in: e.target.value })} className={`${inputCls} w-24`} /></label>
                                            <label>cache <input value={d.cache} onChange={e => set({ cache: e.target.value })} className={`${inputCls} w-20`} /></label>
                                            <label>saída <input value={d.out} onChange={e => set({ out: e.target.value })} className={`${inputCls} w-24`} /></label>
                                            <span className="text-gray-400">tokens médios por {a.unit}</span>
                                          </>
                                        ) : (
                                          <>
                                            <input value={d.qty} onChange={e => set({ qty: e.target.value })} className={`${inputCls} w-20`} />
                                            <span>×</span>
                                            <select
                                              value={d.rate}
                                              onChange={e => set({ rate: e.target.value })}
                                              className="px-1 py-1 border border-gray-300 rounded-md text-xs max-w-[320px]"
                                            >
                                              {Array.from(new Map(table.rates.map(r => [r.key, r])).values()).map(r => (
                                                <option key={r.key} value={r.key}>{r.label} ({r.unit})</option>
                                              ))}
                                            </select>
                                          </>
                                        )}
                                        <span className="ml-auto font-mono text-gray-700">{formatNum(componentCredits(d), 6)} créd.</span>
                                      </div>
                                    );
                                  })}
                                  <div className="flex justify-end gap-2 pt-1">
                                    <button
                                      onClick={() => setActionDrafts(prev => ({ ...prev, [key]: a.components.map(toDraft) }))}
                                      className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-600"
                                    >
                                      Desfazer
                                    </button>
                                    <button
                                      onClick={() => saveAction(key)}
                                      disabled={busy === `action:${key}`}
                                      className="px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                                    >
                                      {busy === `action:${key}` ? '...' : 'Salvar receita'}
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        {/* Custos de provedor */}
        <div className="p-4 space-y-6 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">Custos de provedor</h3>
          {providers.map(provider => {
            const rows = table.rates.filter(r => r.provider === provider);
            return (
              <div key={provider}>
                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-2">{PROVIDER_LABELS[provider] || provider}</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                        <th className="py-2 pr-3">Item</th>
                        <th className="py-2 px-3">Unidade</th>
                        <th className="py-2 px-3">Vigência</th>
                        <th className="py-2 px-3">Custo</th>
                        <th className="py-2 px-3 text-right">Em R$</th>
                        <th className="py-2 px-3 text-right">Créditos</th>
                        <th className="py-2 pl-3 text-right">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(r => {
                        const draft = drafts[r.id] || { cost: String(r.cost), currency: r.currency };
                        const dirty = parseNum(draft.cost) !== r.cost || draft.currency !== r.currency;
                        const preview = rowPreview(r);
                        return (
                          <tr key={r.id} className={`border-b border-gray-50 last:border-0 ${r.active ? '' : 'opacity-50'}`}>
                            <td className="py-2 pr-3">
                              <div className="font-medium text-gray-800">{r.label}</div>
                              <div className="text-[11px] text-gray-400 font-mono">{r.key}</div>
                            </td>
                            <td className="py-2 px-3 text-gray-500">{r.unit}</td>
                            <td className="py-2 px-3 text-gray-500 whitespace-nowrap">
                              {formatDate(r.effectiveFrom)}
                              {r.current && <span className="ml-2 text-[10px] rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 font-semibold">vigente</span>}
                              {r.scheduled && <span className="ml-2 text-[10px] rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 font-semibold">agendado</span>}
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex items-center gap-1">
                                <select
                                  value={draft.currency}
                                  onChange={e => setDrafts(prev => ({ ...prev, [r.id]: { ...draft, currency: e.target.value as ProviderCurrency } }))}
                                  className="px-1 py-1.5 border border-gray-300 rounded-md text-xs"
                                >
                                  {(['BRL', 'USD', 'CREDITO'] as ProviderCurrency[]).map(c => <option key={c} value={c}>{CURRENCY_LABEL[c]}</option>)}
                                </select>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={draft.cost}
                                  onChange={e => setDrafts(prev => ({ ...prev, [r.id]: { ...draft, cost: e.target.value } }))}
                                  onKeyDown={e => e.key === 'Enter' && saveRate(r)}
                                  className="w-24 px-2 py-1.5 border border-gray-300 rounded-md font-mono text-right focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                              </div>
                            </td>
                            <td className="py-2 px-3 text-right font-mono text-gray-500">{preview ? formatNum(preview.brl, 6) : '—'}</td>
                            <td className="py-2 px-3 text-right font-mono font-semibold text-gray-800">{preview ? formatNum(preview.credits, 6) : '—'}</td>
                            <td className="py-2 pl-3">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => saveRate(r)}
                                  disabled={busy === r.id || !dirty}
                                  className="px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
                                >
                                  {busy === r.id ? '...' : 'Salvar'}
                                </button>
                                {r.current && (
                                  <button
                                    onClick={() => setSchedule({ key: r.key, cost: String(r.cost), date: '' })}
                                    title="Cadastrar um novo preço com data de vigência"
                                    className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50"
                                  >
                                    Agendar
                                  </button>
                                )}
                                <button
                                  onClick={() => toggleActive(r)}
                                  disabled={busy === r.id}
                                  className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                                >
                                  {r.active ? 'Desativar' : 'Ativar'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <LlmAreaModels table={table} onSaved={reloadAfterChange} />

      {schedule && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setSchedule(null)}>
          <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-gray-900">Agendar novo preço</h3>
            <p className="mt-1 text-xs text-gray-500 font-mono">{schedule.key}</p>
            <label className="mt-4 block text-xs font-medium text-gray-600">
              Custo (mesma moeda da linha vigente)
              <input
                type="text"
                inputMode="decimal"
                value={schedule.cost}
                onChange={e => setSchedule({ ...schedule, cost: e.target.value })}
                className="mt-1 block w-full px-2 py-1.5 border border-gray-300 rounded-md font-mono text-sm"
              />
            </label>
            <label className="mt-3 block text-xs font-medium text-gray-600">
              Vale a partir de
              <input
                type="date"
                value={schedule.date}
                onChange={e => setSchedule({ ...schedule, date: e.target.value })}
                className="mt-1 block w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setSchedule(null)} className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 text-gray-600">Cancelar</button>
              <button
                onClick={saveSchedule}
                disabled={busy === `schedule:${schedule.key}`}
                className="px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
              >
                Agendar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
